/**
 * Zoom de escritorio para el visor ampliado de la ficha.
 *
 * El tema resuelve el zoom solo para dedos: `drag-zoom-wrapper.js` escucha
 * touchstart/touchmove y se apaga con `if (!isMobileBreakpoint()) return;`, y
 * todo el CSS que hace caber la foto vive dentro de @media (max-width: 749px).
 * En un computador la foto se pintaba a ancho completo -- 1440x2560 px en una
 * pantalla de 900 -- así que al abrir el zoom solo se veía el tercio de arriba.
 * Encima, el <li> trae `on:click="/close"`: el clic cerraba el visor.
 *
 * Aquí la foto entra completa y el clic la amplía tomando como centro el punto
 * señalado; al mover el ratón, la ampliación lo sigue. Segundo clic, tecla Esc,
 * la X o un clic fuera de la foto, y se vuelve como estaba.
 */
(() => {
  const ESCRITORIO = '(min-width: 750px)';
  const GALERIA = '.dialog-zoomed-gallery';
  const FOTO = '.dialog-zoomed-gallery .product-media__image';
  const MARCO = '.product-media__drag-zoom-wrapper';
  const GUARDADO = 'data-ec-onclick';

  const enEscritorio = () => window.matchMedia(ESCRITORIO).matches;

  let ampliada = null;
  let pendiente = null;

  /**
   * El tema enlaza el cierre con un `on:click` que escucha en `document` y en
   * fase de captura, de modo que se adelanta a cualquier handler propio: no
   * basta con detener la propagación. En escritorio le retiramos el atributo a
   * las fotos y devolvemos el cierre nosotros; en móvil queda intacto.
   */
  function soltarCierre() {
    // `on:click` lleva dos puntos: como selector CSS habría que escaparlo, y un
    // escape mal puesto deja el selector inválido y lanza. Se filtra a mano.
    for (const li of document.querySelectorAll(`${GALERIA} li`)) {
      const cierre = li.getAttribute('on:click');
      if (!cierre) continue;
      li.setAttribute(GUARDADO, cierre);
      li.removeAttribute('on:click');
    }
  }

  function devolverCierre() {
    for (const li of document.querySelectorAll(`${GALERIA} li[${GUARDADO}]`)) {
      li.setAttribute('on:click', li.getAttribute(GUARDADO));
      li.removeAttribute(GUARDADO);
    }
  }

  /**
   * Caja de la foto sin ampliar. offsetWidth/offsetHeight ignoran el transform,
   * así que siguen sirviendo con la foto ya ampliada. Si `object-fit: contain`
   * dejó franjas vacías, se descuentan: sin eso la lupa se iría al vacío.
   */
  function caja(img) {
    const marco = img.closest(MARCO) || img.parentElement;
    const r = marco.getBoundingClientRect();
    const ancho = img.offsetWidth;
    const alto = img.offsetHeight;
    if (!ancho || !alto) return null;

    const x = r.left + (r.width - ancho) / 2;
    const y = r.top + (r.height - alto) / 2;

    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return { x, y, ancho, alto, franjaX: 0, franjaY: 0 };

    const escala = Math.min(ancho / nw, alto / nh);
    return {
      x,
      y,
      ancho,
      alto,
      franjaX: (ancho - nw * escala) / 2,
      franjaY: (alto - nh * escala) / 2,
    };
  }

  function centra(img, evento) {
    const c = caja(img);
    if (!c) return;
    const dentro = (valor, franja, medida) =>
      Math.max(franja, Math.min(medida - franja, valor)) / medida * 100;

    const x = dentro(evento.clientX - c.x, c.franjaX, c.ancho);
    const y = dentro(evento.clientY - c.y, c.franjaY, c.alto);
    img.style.transformOrigin = `${x.toFixed(2)}% ${y.toFixed(2)}%`;
  }

  function reduce() {
    if (!ampliada) return;
    ampliada.removeAttribute('data-ec-zoom');
    ampliada.style.transformOrigin = '';
    ampliada = null;
  }

  function cierraVisor(galeria) {
    const x = galeria.closest('dialog')?.querySelector('.dialog-zoomed-gallery__close-button');
    if (x instanceof HTMLElement) x.click();
  }

  document.addEventListener(
    'click',
    (evento) => {
      const galeria = evento.target.closest(GALERIA);
      if (!galeria) return;
      if (!enEscritorio()) return;

      const img = evento.target.closest(FOTO);
      if (!img) {
        reduce();
        cierraVisor(galeria);
        return;
      }

      const yaEstaba = img === ampliada;
      reduce();
      if (yaEstaba) return;

      centra(img, evento);
      img.setAttribute('data-ec-zoom', '');
      ampliada = img;
    },
    true
  );

  document.addEventListener(
    'mousemove',
    (evento) => {
      if (!ampliada || pendiente) return;
      pendiente = requestAnimationFrame(() => {
        pendiente = null;
        if (ampliada) centra(ampliada, evento);
      });
    },
    { passive: true }
  );

  // El <dialog> nativo no burbujea su evento `close`: hay que oírlo en captura.
  document.addEventListener('close', reduce, true);

  function ajusta() {
    reduce();
    if (enEscritorio()) soltarCierre();
    else devolverCierre();
  }

  /**
   * Al abrirse, el visor vuelve a pintar sus <li> y con ellos reaparece el
   * `on:click` de cierre. Por eso no basta con soltarlo una vez al cargar.
   */
  function vigila() {
    const observador = new MutationObserver(() => {
      if (enEscritorio()) soltarCierre();
    });
    for (const visor of document.querySelectorAll('zoom-dialog')) {
      observador.observe(visor, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['on:click'],
      });
    }
  }

  window.addEventListener('resize', ajusta);
  // La galería ampliada se pinta con la página, pero el script va con `defer`:
  // puede llegar antes o después según la caché, así que cubrimos los dos casos.
  function arranca() {
    ajusta();
    vigila();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arranca);
  else arranca();
})();
